import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { X } from 'lucide-react';
import { EMOJI_DATA, type EmojiCategory, type EmojiEntry } from './emojiData';

interface EmojiPickerProps {
  value: string | undefined;
  onChange: (emoji: string | undefined) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  className?: string;
  id?: string;
}

const CATEGORY_ORDER: EmojiCategory[] = [
  'Smileys',
  'People',
  'Animals',
  'Food',
  'Activities',
  'Travel',
  'Objects',
  'Symbols',
];

const CATEGORY_ICONS: Record<EmojiCategory, string> = {
  Smileys: '😀',
  People: '👋',
  Animals: '🐶',
  Food: '🍎',
  Activities: '⚽',
  Travel: '✈️',
  Objects: '💡',
  Symbols: '❤️',
  Flags: '🏁',
};

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

const filterEmojis = (query: string): EmojiEntry[] => {
  const lower = query.toLowerCase().trim();
  if (!lower) return EMOJI_DATA;

  return EMOJI_DATA.filter(
    (entry) =>
      entry.name.includes(lower) ||
      entry.keywords.some((kw) => kw.includes(lower))
  );
};

const groupByCategory = (
  entries: EmojiEntry[]
): Partial<Record<EmojiCategory, EmojiEntry[]>> => {
  const groups: Partial<Record<EmojiCategory, EmojiEntry[]>> = {};
  for (const entry of entries) {
    if (!groups[entry.category]) {
      groups[entry.category] = [];
    }
    groups[entry.category]!.push(entry);
  }
  return groups;
};

export const EmojiPicker = ({
  value,
  onChange,
  placeholder = '🔍 Search emoji...',
  label,
  helperText,
  className,
  id,
}: EmojiPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredEmojis = useMemo(() => filterEmojis(searchQuery), [searchQuery]);
  const groupedEmojis = useMemo(() => groupByCategory(filteredEmojis), [filteredEmojis]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleSelectEmoji = (emoji: string) => {
    onChange(emoji);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const displayValue = isOpen ? searchQuery : '';

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div ref={containerRef} className="relative">
        <div className="relative">
          {value && !isOpen && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg leading-none pointer-events-none">
              {value}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            id={id}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={value && !isOpen ? '' : placeholder}
            className={`${inputClass} ${value && !isOpen ? 'pl-10' : ''} pr-8`}
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100"
              title="Clear emoji"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {filteredEmojis.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-400">
                No emojis found
              </div>
            ) : (
              CATEGORY_ORDER.map((category) => {
                const emojis = groupedEmojis[category];
                if (!emojis?.length) return null;

                return (
                  <div key={category}>
                    <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100 flex items-center gap-1.5">
                      <span>{CATEGORY_ICONS[category]}</span>
                      {category}
                    </div>
                    <div className="grid grid-cols-8 gap-0.5 p-1.5">
                      {emojis.map((entry) => (
                        <button
                          key={entry.emoji}
                          type="button"
                          onClick={() => handleSelectEmoji(entry.emoji)}
                          className="w-8 h-8 flex items-center justify-center text-lg rounded hover:bg-blue-50 transition-colors cursor-pointer"
                          title={entry.name}
                        >
                          {entry.emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {helperText && (
        <p className="text-xs text-gray-400 mt-1">{helperText}</p>
      )}
    </div>
  );
};
